/**
 * Llamadas HTTP del dominio Estadísticas avanzadas.
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials:
 * 'include'`, el `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request, así
 * que cada métrica se calcula para la sede activa sin que la feature toque la
 * cabecera a mano (el backend resuelve y valida la sede con `resolve_api_sede`).
 *
 * Los endpoints son **read-only**: el backend ya devuelve dicts JSON-safe con una
 * lista blanca de claves (`domains/stats.py`); aquí solo se tipa la respuesta. Un
 * `ApiError` (401/403/400 por sede/5xx) lo propaga el cliente y lo gestionan los
 * hooks/pantalla.
 *
 * Cubre la superficie de analytics de `application/api/domains/stats.py` que la
 * pantalla de Estadísticas avanzadas necesita (réplica del contenido del SSR
 * `advanced_stats_with_charts.html`): efectividad, órdenes por día, tiempos,
 * series de órdenes/ingresos, ventas por vendedor y pedidos de láminas recibidos.
 * Las rutas de diagnóstico (`stats/diagnosis`/`stats/simple`/`stats/debug_today`)
 * NO se exponen aquí: el SSR no las pinta en la página.
 */
import { api } from "@/lib/api";

import type { OrdersPeriod, RevenuePeriod, SellerPeriod } from "./schemas";
import type {
  DailyOrdersStats,
  EffectivenessStats,
  OrdersChartStats,
  OrdersTimingStats,
  PedidosLaminasStats,
  RevenueChartStats,
  SalesBySellerStats,
} from "./types";

/**
 * Efectividad de entregas de la sede activa (`GET /api/v1/stats/effectiveness`).
 * Permiso backend: `view_orders`. Alimenta el KPI de efectividad y su desglose.
 */
export async function fetchEffectiveness(): Promise<EffectivenessStats> {
  const response = await api.get("stats/effectiveness");
  return (await response.json()) as EffectivenessStats;
}

/**
 * Órdenes por día (últimos 30 con actividad) de la sede activa
 * (`GET /api/v1/stats/daily_orders`). Permiso backend: `view_orders`. Alimenta los
 * KPIs del panel (por período) y la gráfica de histórico diario.
 */
export async function fetchDailyOrders(): Promise<DailyOrdersStats> {
  const response = await api.get("stats/daily_orders");
  return (await response.json()) as DailyOrdersStats;
}

/**
 * Tiempos creación->entrega y detalle por orden de la sede activa
 * (`GET /api/v1/stats/orders_timing`). Permiso backend: `view_orders`. Alimenta los
 * KPIs de pendientes/anuladas y la gráfica de estado actual.
 */
export async function fetchOrdersTiming(): Promise<OrdersTimingStats> {
  const response = await api.get("stats/orders_timing");
  return (await response.json()) as OrdersTimingStats;
}

/**
 * Serie temporal de cantidad de órdenes (`GET /api/v1/stats/orders_chart`).
 * Permiso backend: `view_orders`. `period`: `day` (defecto), `week`, `month`.
 */
export async function fetchOrdersChart(
  period: OrdersPeriod,
): Promise<OrdersChartStats> {
  const response = await api.get("stats/orders_chart", {
    searchParams: { period },
  });
  return (await response.json()) as OrdersChartStats;
}

/**
 * Serie temporal de ingresos (`GET /api/v1/stats/revenue_chart`). Permiso backend:
 * `view_orders`. `period`: `week`, `month` (defecto), `year`.
 */
export async function fetchRevenueChart(
  period: RevenuePeriod,
): Promise<RevenueChartStats> {
  const response = await api.get("stats/revenue_chart", {
    searchParams: { period },
  });
  return (await response.json()) as RevenueChartStats;
}

/**
 * Ventas por vendedor/atendido (`GET /api/v1/stats/sales_by_seller`). Permiso
 * backend: `view_orders`. `period`: `day`, `week` (defecto), `month`, `year`.
 */
export async function fetchSalesBySeller(
  period: SellerPeriod,
): Promise<SalesBySellerStats> {
  const response = await api.get("stats/sales_by_seller", {
    searchParams: { period },
  });
  return (await response.json()) as SalesBySellerStats;
}

/**
 * Pedidos de láminas recibidos (`GET /api/v1/stats/pedidos_laminas`). Permiso
 * backend: `view_pedidos_laminas`. `period`: `day` (defecto), `week`, `month`. El
 * conteo es global (igual que el SSR), pero el backend exige una sede válida del
 * request por consistencia del contrato; la aporta el cliente.
 */
export async function fetchPedidosLaminas(
  period: OrdersPeriod,
): Promise<PedidosLaminasStats> {
  const response = await api.get("stats/pedidos_laminas", {
    searchParams: { period },
  });
  return (await response.json()) as PedidosLaminasStats;
}
